import { useEffect } from "react";
import { publicMediaSrc, toConfiguredAbsoluteUrl } from "../config/api";
import { productHref } from "./CategoryCard";
import productsShareImage from "../assets/all-products-bg.jpg";

const DEFAULT_DOCUMENT_TITLE = "Steel, Hardware & Industrial Supplies Malaysia";
const DEFAULT_DOCUMENT_DESCRIPTION =
  "Steel, hardware, fasteners, roofing, paint and industrial products for contractors across Malaysia. Request a quote today.";
const COLLECTION_JSON_LD_ID = "products-collection-jsonld";
const BREADCRUMB_JSON_LD_ID = "products-breadcrumb-jsonld";
const ITEMLIST_JSON_LD_ID = "products-itemlist-jsonld";

function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el && el.dataset.productsSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.productsSeo = "true";
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el && el.dataset.productsSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  el.dataset.productsSeo = "true";
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function prune(value) {
  if (Array.isArray(value)) {
    const next = value.map(prune).filter((item) => item !== undefined);
    return next.length ? next : undefined;
  }
  if (value && typeof value === "object") {
    const next = {};
    Object.keys(value).forEach((key) => {
      const item = prune(value[key]);
      if (item !== undefined && item !== "") next[key] = item;
    });
    return Object.keys(next).length ? next : undefined;
  }
  if (value === undefined || value === null || value === "") return undefined;
  return value;
}

function clearProductsSeo() {
  document.head.querySelectorAll("[data-products-seo='true']").forEach((el) => el.remove());
  upsertJsonLd(COLLECTION_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
  upsertJsonLd(ITEMLIST_JSON_LD_ID, null);
}

export function productsPageSeo(contact) {
  const siteName = cleanText(contact?.siteName);
  if (!siteName) return null;
  return {
    siteName,
    title: `Industrial Products & Supplies Malaysia | ${siteName}`,
    description: `Browse steel, hardware, fasteners, roofing, paint and industrial products from ${siteName}. Product sourcing and delivery available across Malaysia.`,
    imageAlt: `Industrial products available from ${siteName}`,
    itemListName: `Products from ${siteName}`,
  };
}

export function productCardImageAlt(product, contact) {
  const explicit = cleanText(product?.imageAlt);
  if (explicit) return explicit;
  const name = cleanText(product?.productName);
  const siteName = cleanText(contact?.siteName);
  if (name && siteName) return `${name} available from ${siteName}`;
  return name;
}

function applyNeutralMetadata(canonicalUrl) {
  document.title = DEFAULT_DOCUMENT_TITLE;
  upsertMeta("name", "description", DEFAULT_DOCUMENT_DESCRIPTION);
  upsertLink("canonical", canonicalUrl);
  upsertMeta("name", "robots", "");
  upsertJsonLd(COLLECTION_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
  upsertJsonLd(ITEMLIST_JSON_LD_ID, null);
}

function ProductsSeo({
  contact,
  contactStatus = "loading",
  productsReady = false,
  items = [],
  itemCount = 0,
  page = 1,
  pageSize = 12,
}) {
  useEffect(() => {
    const canonicalUrl = toConfiguredAbsoluteUrl("/products");
    const homeUrl = toConfiguredAbsoluteUrl("/");
    const websiteId = `${String(homeUrl || "/").replace(/\/$/, "")}/#website`;

    if (contactStatus === "loading") {
      applyNeutralMetadata(canonicalUrl);
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearProductsSeo();
      };
    }

    const seo = productsPageSeo(contact);
    if (!seo) {
      applyNeutralMetadata(canonicalUrl);
      if (process.env.NODE_ENV !== "production") {
        console.info("Products SEO: contact.siteName unavailable; using application title fallback.");
      }
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearProductsSeo();
      };
    }

    const imageUrl = toConfiguredAbsoluteUrl(productsShareImage);

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertLink("canonical", canonicalUrl);
    upsertMeta("name", "robots", "");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", imageUrl ? seo.imageAlt : "");
    upsertMeta("property", "og:site_name", seo.siteName);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertMeta("name", "twitter:image:alt", imageUrl ? seo.imageAlt : "");

    upsertJsonLd(
      COLLECTION_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": canonicalUrl ? `${canonicalUrl}#webpage` : undefined,
        url: canonicalUrl || undefined,
        name: seo.title,
        description: seo.description,
        isPartOf: { "@id": websiteId },
        breadcrumb: canonicalUrl ? { "@id": `${canonicalUrl}#breadcrumb` } : undefined,
      })
    );

    upsertJsonLd(
      BREADCRUMB_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": canonicalUrl ? `${canonicalUrl}#breadcrumb` : undefined,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "Products", item: canonicalUrl },
        ],
      })
    );

    // Client-side filter/sort/pagination: ItemList is rebuilt from the current
    // matching page whenever those props change. Empty matches omit the list.
    const listItems = productsReady
      ? (Array.isArray(items) ? items : []).filter((item) => {
          const href = productHref(item);
          return item?.productName && href && href !== "/products";
        })
      : [];

    if (listItems.length) {
      const start = Math.max(0, (Number(page) || 1) - 1) * (Number(pageSize) || 12);
      upsertJsonLd(
        ITEMLIST_JSON_LD_ID,
        prune({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": canonicalUrl ? `${canonicalUrl}#product-list` : undefined,
          name: seo.itemListName,
          numberOfItems: itemCount,
          itemListElement: listItems.map((item, index) => {
            const imagePath = publicMediaSrc(item.image);
            return {
              "@type": "ListItem",
              position: start + index + 1,
              name: item.productName,
              url: toConfiguredAbsoluteUrl(productHref(item)),
              image: imagePath ? toConfiguredAbsoluteUrl(imagePath) : undefined,
            };
          }),
        })
      );
    } else {
      upsertJsonLd(ITEMLIST_JSON_LD_ID, null);
    }

    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
      clearProductsSeo();
    };
  }, [contact, contactStatus, productsReady, items, itemCount, page, pageSize]);

  return null;
}

export default ProductsSeo;
